import { TGetCompanySpecificIndustryListResDto } from "@/api/services/global/master/dtos/companySpecificIndustry/get-master-company-specific-industry-list.dto";
import { TGetNationalityListResDto } from "@/api/services/global/master/dtos/nationality/get-master-nationality-list.dto";
import { TGetResidenceStatusListResDto } from "@/api/services/global/master/dtos/residencePeriod/get-master-residence-status-list.dto";
import { TGetMainIndustryListResDto } from "@/api/services/global/master/dtos/get-master-main-industry-list.dto";
import { TOption } from "@/types/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { globalApi } from "@/utils/api";
import { toNumber } from "@/utils/number";
import { CACHED_TIME } from "@/constants/common";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { TGetLanguageTestListResDto } from "@/api/services/global/master/dtos/languageTest/get-master-language-test-list.dto";
import { TGetSpecificSkillCertificateListResDto } from "@/api/services/global/master/dtos/specificSkillCertificate/get-master-specific-skill-certificate";
import { TGetMajorListResDto } from "@/api/services/global/master/dtos/jobTypes/get-master-major-list.dto";
import { TGetAttachmentDocumentListResDto } from "@/api/services/global/master/dtos/attchmentDocumentType/get-attachment-document-type-list.dto";
import { TGetBusinessClassificationListResDto } from "@/api/services/global/master/dtos/businessClassification/get-master-business-classification-list.dto";
import { TGetTaxRateListResDto } from "@/api/services/global/master/dtos/taxRate/get-master-tax-rate-list.dto";

//nationalities
export const fetchNationalityListApi = async (): Promise<TGetNationalityListResDto> => {
  const rs = await globalApi.get<TGetNationalityListResDto>(`/master/regions/nationalities`);
  return rs.data;
};
export const useGetNationalityList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_NATIONALITY],
    queryFn: () => fetchNationalityListApi(),
  });

  const nationalityOptions = useMemo(
    () => (data ? data.data.map((item) => ({ label: item.nationalityName, value: item.id, hidden: item.isHidden })) : []),
    [data],
  );

  return { data: data?.data ?? [], nationalityOptions, ...rest };
};

//residence-periods
export const fetchResidenceStatusListApi = async (): Promise<TGetResidenceStatusListResDto> => {
  const rs = await globalApi.get<TGetResidenceStatusListResDto>(`/master/residence-statuses`);
  return rs.data;
};

export const useGetResidenceStatusList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_RESIDENCE_STATUS],
    queryFn: () => fetchResidenceStatusListApi(),
  });

  const residenceStatusOptions = useMemo(
    () => (data ? data.data.map((item) => ({ label: item.residenceStatus, value: item.id, hidden: item.isHidden })) : []),
    [data],
  );

  return { data: data?.data ?? [], residenceStatusOptions, ...rest };
};

//company-specific-industry
export const fetchCompanySpecificIndustryListApi = async (): Promise<TGetCompanySpecificIndustryListResDto> => {
  const rs = await globalApi.get<TGetCompanySpecificIndustryListResDto>(`/master/company-specific-industries`);
  return rs.data;
};

export const useGetCompanySpecificIndustryList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_COMPANY_SPECIFIC_INDUSTRY],
    queryFn: () => fetchCompanySpecificIndustryListApi(),
  });

  const companySpecificIndustryOptions = useMemo(
    () => (data ? data.data.map((item) => ({ label: item.companySpecificIndustry, value: toNumber(item.id), hidden: item.isHidden })) : []),
    [data],
  );

  const getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry = useCallback(
    (companySpecificIndustryId: number | string) => {
      const foundCompanySpecificIndustry = data ? data.data.find((item) => toNumber(item.id) === toNumber(companySpecificIndustryId)) : undefined;
      if (!foundCompanySpecificIndustry) return [];
      const options =
        foundCompanySpecificIndustry?.companySpecificBusinessClassifications?.map((item) => ({
          label: item.companySpecificBusinessClassificationName,
          value: item.id,
          hidden: item.isHidden,
        })) || [];
      return options;
    },
    [data],
  );

  return {
    data: data?.data ?? [],
    companySpecificIndustryOptions,
    getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry,
    ...rest,
  };
};

// main industry
export const fetchMainIndustryListApi = async (): Promise<TGetMainIndustryListResDto> => {
  const rs = await globalApi.get<TGetMainIndustryListResDto>("/master/main-industries");
  return rs.data;
};

export const useGetMainIndustryList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_MAIN_INDUSTRY],
    queryFn: () => fetchMainIndustryListApi(),
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  const mainIndustryOptions: TOption[] = useMemo(
    () => (data ? data?.data.map((item) => ({ label: item.mainIndustry, value: item.id })) : []),
    [data],
  );

  return { data: data?.data ?? [], mainIndustryOptions, ...rest };
};

//language-test
export const fetchLanguageTestListApi = async (): Promise<TGetLanguageTestListResDto> => {
  const rs = await globalApi.get<TGetLanguageTestListResDto>("/master/language-tests");
  return rs.data;
};

export const useGetLanguageTestList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_LANGUAGE_TEST],
    queryFn: () => fetchLanguageTestListApi(),
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  const languageTestOptions = useMemo(
    () => (data ? data.data.map((item) => ({ label: item.testName, value: item.id, hidden: item.isHidden })) : []),
    [data],
  );

  const languageTestLevelOptions = useMemo(() => {
    if (!data) return [];

    return data.data.flatMap((item) => {
      if (item.levels && item.levels.length > 0) {
        const levelOptions = item.levels.map((lvl) => ({
          label: `${item.testName} ・ ${lvl.level}`,
          value: lvl.id,
          hidden: item.isHidden || lvl.isHidden,
        }));

        const allLevelsHidden = levelOptions.every((opt) => opt.hidden);

        if (allLevelsHidden) {
          return [
            ...levelOptions,
            {
              label: item.testName,
              value: -item.id,
              hidden: item.isHidden,
            },
          ];
        }

        return levelOptions;
      }

      return [
        {
          label: item.testName,
          value: -item.id,
          hidden: item.isHidden,
        },
      ];
    });
  }, [data]);

  return { data: data?.data ?? [], languageTestOptions, languageTestLevelOptions, ...rest };
};

// specific skill certificate

export const fetchSpecificSkillCertificates = async (): Promise<TGetSpecificSkillCertificateListResDto> => {
  const rs = await globalApi.get<TGetSpecificSkillCertificateListResDto>("/master/specific-skill-certificates");
  return rs.data;
};

export const useGetSpecificSkillCertificateList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_SPECIFIC_SKILL_CERTIFICATE],
    queryFn: () => fetchSpecificSkillCertificates(),
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  const specificSkillCertificateOptions = useMemo(
    () => (data ? data.data.map((item) => ({ label: item.certificateName, value: item.id, hidden: item.isHidden })) : []),
    [data],
  );

  const getCertificatesByIndustryId = useCallback(
    (industryId: number) => {
      return data?.data
        ? data.data
            .filter((certificate) =>
              certificate.specificSkillCertificateCompanySpecificIndustries.some((item) => {
                return Number(industryId) === parseInt(item.companySpecificIndustryId) && !item.isHidden;
              }),
            )
            .map((item) => ({
              label: item.certificateName,
              value: item.id,
              hidden: item.isHidden,
            }))
        : [];
    },
    [data],
  );

  return { data: data?.data ?? [], specificSkillCertificateOptions, getCertificatesByIndustryId, ...rest };
};

//job-types
export const fetchMajorListApi = async (): Promise<TGetMajorListResDto> => {
  const rs = await globalApi.get<TGetMajorListResDto>(`/master/majors`);
  return rs.data;
};

export const useGetMajorList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_MAJOR],
    queryFn: () => fetchMajorListApi(),
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  const majorOptions = useMemo(
    () => (data ? data.data.map((item) => ({ label: item.majorName, value: item.id, hidden: item.isHidden })) : []),
    [data],
  );

  const getJobTypeListOptionByMajor = useCallback(
    (majorId: number | string) => {
      const foundMajor = data ? data.data.find((item) => toNumber(item.id) === toNumber(majorId)) : undefined;
      if (!foundMajor) return [];
      const options = foundMajor.jobTypes.map((item) => ({ label: item.jobName, value: item.id, hidden: item.isHidden }));
      return options;
    },
    [data],
  );

  return { data: data?.data ?? [], majorOptions, getJobTypeListOptionByMajor, ...rest };
};

//attachment-document-types
export const fetchAttachmentDocumentTypeListApi = async (): Promise<TGetAttachmentDocumentListResDto> => {
  const rs = await globalApi.get<TGetAttachmentDocumentListResDto>(`/master/attachment-document-types`);
  return rs.data;
};
export const useGetAttachmentDocumentTypeList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_ATTACHMENT_DOCUMENT_TYPE],
    queryFn: () => fetchAttachmentDocumentTypeListApi(),
  });

  const attachmentDocumentTypeOptions = useMemo(
    () => (data ? data.data.map((item) => ({ label: item.documentTypeName, color: item.color, value: item.id, hidden: item.isHidden })) : []),
    [data],
  );

  return { data: data?.data ?? [], attachmentDocumentTypeOptions, ...rest };
};

// tax-rates (税率マスタ)
export const fetchTaxRateListApi = async (): Promise<TGetTaxRateListResDto> => {
  const rs = await globalApi.get<TGetTaxRateListResDto>("/master/tax-rates");
  return rs.data;
};

export const useGetTaxRateList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_TAX_RATE],
    queryFn: () => fetchTaxRateListApi(),
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  const taxRateOptions: TOption[] = useMemo(
    () => (data ? data.data.map((item) => ({ label: item.displayName, value: String(item.id), hidden: item.isHidden })) : []),
    [data],
  );

  return { data: data?.data ?? [], taxRateOptions, ...rest };
};

// Business classification
export const fetchBusinessClassificationListApi = async (): Promise<TGetBusinessClassificationListResDto> => {
  const rs = await globalApi.get<TGetBusinessClassificationListResDto>(`/master/business-classifications`);
  return rs.data;
};

export const useGetBusinessClassificationList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.MASTER_BUSINESS_CLASSIFICATION],
    queryFn: () => fetchBusinessClassificationListApi(),
  });

  const businessClassificationOptions = useMemo(
    () => (data ? data.data.map((item) => ({ label: item.businessCategory, value: item.id, hidden: item.isHidden })) : []),
    [data],
  );

  return { data: data?.data ?? [], businessClassificationOptions, ...rest };
};
